import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, X, Sparkles, Zap, ShieldCheck, QrCode, Wallet, ArrowRight, Star } from "lucide-react";
import HeroDemo from "@/pages/HeroDemo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
const startGoogleSignup = () => {
  const redirectUrl = window.location.origin + "/store"; // dashboard, not landing
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.24 1.05-3.71 1.05-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.48 12c0-.72.13-1.42.36-2.09V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
  </svg>
);

const compareRows = [
  { label: "Prints per day", free: "10 · forever", paid: "Unlimited" },
  { label: "Printers", free: "1", paid: "Up to 3+" },
  { label: "Pay-at-counter", free: true, paid: true },
  { label: "Razorpay online", free: true, paid: true },
  { label: "Dashboard & analytics", free: true, paid: "Advanced" },
  { label: "Store logo & branding", free: true, paid: true },
  { label: "Priority support", free: false, paid: true },
  { label: "Multi-branch", free: false, paid: "Enterprise" },
];

const testimonials = [
  { name: "Ramesh S.", shop: "Xerox Point, HSR Layout", quote: "Free-forever meant I could try the whole thing on a Sunday and be running by Monday morning. Cash flow tracked itself." },
  { name: "Anita K.", shop: "Copy Corner, Whitefield", quote: "10 prints/day covers a slow weekday. Days I'm busier I upgrade for that month — no lock-in, no surprises." },
  { name: "Mohit P.", shop: "Print Zone, Koramangala", quote: "The QR poster killed queues. Customers just scan, order, and I hand over the print when it's done." },
];

export default function GetStarted() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-white overflow-x-hidden">
      {/* NAV */}
      <header className="max-w-6xl mx-auto px-5 sm:px-6 pt-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="Print Plus" className="w-10 h-10 object-contain" />
          <div className="leading-tight">
            <div className="font-display text-lg font-bold">Print Plus</div>
            <div className="text-[9px] uppercase tracking-widest text-orange-500 font-semibold -mt-0.5">by AI Upaya</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 hidden sm:inline">Have an account?</Link>
          <Button onClick={startGoogleSignup} className="bg-slate-900 hover:bg-slate-800 h-9 text-xs sm:text-sm" data-testid="nav-google-btn">
            <GoogleIcon className="w-4 h-4 mr-1.5" /> Start free
          </Button>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 pt-10 pb-14 md:pt-16 md:pb-20 grid md:grid-cols-2 gap-10 items-center overflow-hidden">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-[11px] font-semibold px-3 py-1 mb-4">
            <Sparkles className="w-3 h-3" /> Free forever · 10 prints/day
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-slate-900">
            Run your Xerox shop on the <span className="text-orange-500">world's first free-forever</span> print SaaS.
          </h1>
          <p className="mt-5 text-base text-slate-600 max-w-lg">
            Customers scan a QR, upload files, pay at the counter (or online), and their prints come out on your printer — automatically. Ten prints per day, every day, <b>at zero cost, for life</b>. Upgrade only if you outgrow it.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <Button onClick={startGoogleSignup} size="lg" className="bg-orange-500 hover:bg-orange-600 h-12 text-base font-semibold shadow-lg shadow-orange-500/20" data-testid="hero-google-btn">
              <GoogleIcon className="w-5 h-5 mr-2 bg-white rounded-full p-0.5" />
              Start free with Google
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="#how" className="inline-flex items-center justify-center px-4 h-12 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">Watch the 15-sec demo ↓</a>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />No credit card</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />No time limit</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />Gmail sign-in · 20 seconds</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />100% payments to your bank</span>
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-200/50 via-amber-100/50 to-transparent blur-2xl -z-10" />
          <HeroDemo />
          <div className="mt-4 grid grid-cols-3 gap-3 max-w-[320px] mx-auto text-center">
            <div className="rounded-xl bg-white border border-slate-200 p-2.5"><Wallet className="w-4 h-4 mx-auto text-amber-500 mb-1" /><div className="text-[11px] text-slate-600 font-semibold">Cash-first</div></div>
            <div className="rounded-xl bg-white border border-slate-200 p-2.5"><Zap className="w-4 h-4 mx-auto text-orange-500 mb-1" /><div className="text-[11px] text-slate-600 font-semibold">Instant</div></div>
            <div className="rounded-xl bg-white border border-slate-200 p-2.5"><ShieldCheck className="w-4 h-4 mx-auto text-emerald-500 mb-1" /><div className="text-[11px] text-slate-600 font-semibold">Secure</div></div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-6xl mx-auto px-5 sm:px-6 py-12 md:py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs font-mono uppercase tracking-widest text-orange-600 font-semibold">How it works</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 tracking-tight">Sign up in 20 seconds. Start printing in 5 minutes.</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { n: "1", t: "Sign up with Gmail", b: "One click — Google Auth. No forms, no passwords, no credit card." },
            { n: "2", t: "Install the Print Agent", b: "Run the one-click Windows installer on your shop PC. Silent scheduled task." },
            { n: "3", t: "Share your QR", b: "Print & paste the counter poster. Customers scan → upload → configure." },
            { n: "4", t: "Print and collect", b: "Paid or cash-at-counter, jobs print automatically. Cash orders wait for your tap." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="w-8 h-8 rounded-xl bg-orange-500 text-white font-display font-bold grid place-items-center mb-3">{s.n}</div>
              <div className="font-display font-bold text-slate-900">{s.t}</div>
              <div className="text-sm text-slate-500 mt-1 leading-relaxed">{s.b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARE */}
      <section className="max-w-4xl mx-auto px-5 sm:px-6 py-12">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div className="text-xs font-mono uppercase tracking-widest text-orange-600 font-semibold">Free vs Paid</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 tracking-tight">Everything you need. Nothing you don't.</h2>
          <p className="text-slate-500 mt-2">Upgrade only when your shop's volume grows past 10 prints a day.</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="grid grid-cols-3 border-b border-slate-200 bg-slate-50/50 text-xs font-mono uppercase tracking-widest text-slate-500">
            <div className="p-4">Feature</div>
            <div className="p-4 text-center bg-amber-50/60 text-amber-800">Free · Forever</div>
            <div className="p-4 text-center">Paid plans</div>
          </div>
          {compareRows.map((r) => (
            <div key={r.label} className="grid grid-cols-3 border-b border-slate-100 last:border-0 text-sm">
              <div className="p-4 text-slate-700 font-medium">{r.label}</div>
              <div className="p-4 text-center bg-amber-50/40">
                {r.free === true ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> :
                 r.free === false ? <X className="w-4 h-4 text-slate-300 mx-auto" /> :
                 <span className="text-slate-700 font-semibold">{r.free}</span>}
              </div>
              <div className="p-4 text-center">
                {r.paid === true ? <Check className="w-4 h-4 text-emerald-500 mx-auto" /> :
                 r.paid === false ? <X className="w-4 h-4 text-slate-300 mx-auto" /> :
                 <span className="text-slate-700 font-semibold">{r.paid}</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-slate-400 text-center">Paid plans start at ₹499/month. Upgrade any time from your dashboard.</div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-6 py-12 md:py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs font-mono uppercase tracking-widest text-orange-600 font-semibold">Loved by shop owners</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 tracking-tight">Real shops, real cash orders.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {testimonials.map((t) => (
            <div key={t.name} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex gap-0.5 text-orange-500 mb-2">{Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-orange-500" />)}</div>
              <div className="text-sm text-slate-700 leading-relaxed">"{t.quote}"</div>
              <div className="mt-4 pt-3 border-t border-slate-100">
                <div className="font-display font-bold text-slate-900 text-sm">{t.name}</div>
                <div className="text-xs text-slate-500">{t.shop}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-3xl mx-auto px-5 sm:px-6 py-14 md:py-20 text-center">
        <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight text-slate-900">Zero setup cost. Zero excuses.</h2>
        <p className="text-slate-600 mt-4 max-w-xl mx-auto">10 free prints every day, for life. Your customers get a modern QR-scan-and-pay experience. You get a real dashboard, real analytics, and 100% of every rupee they pay.</p>
        <Button onClick={startGoogleSignup} size="lg" className="mt-8 bg-orange-500 hover:bg-orange-600 h-14 px-8 text-base font-semibold shadow-xl shadow-orange-500/25" data-testid="final-google-btn">
          <GoogleIcon className="w-5 h-5 mr-2.5 bg-white rounded-full p-0.5" />
          Start free with Google
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <div className="mt-3 text-[11px] text-slate-400">No credit card · Forever free · Gmail-only sign-up</div>
      </section>

      <footer className="border-t border-slate-200 py-6 px-6 text-center text-xs text-slate-400 pb-24 md:pb-6">
        © 2026 Print Plus by AI Upaya · <Link to="/" className="hover:text-slate-600">Product homepage</Link> · <Link to="/login" className="hover:text-slate-600">Sign in</Link>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur border-t border-slate-200 p-3">
        <Button onClick={startGoogleSignup} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-base font-semibold" data-testid="mobile-sticky-google-btn">
          <GoogleIcon className="w-5 h-5 mr-2 bg-white rounded-full p-0.5" />
          Start free with Google
        </Button>
      </div>
    </div>
  );
}
